import React, { useCallback, useEffect, useState } from 'react';
import { Area, AreaChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../components/ui/chart';
import ProfileMenu from '../components/ProfileMenu';
import TradeChart from '../components/TradeChart';
import { resolveTheme, type Theme } from '../lib/theme';
import { fetchSession } from '../lib/session';

async function api(path: string, opts?: any) {
  const r = await fetch('/api/trading' + path, opts);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || r.statusText);
  return d;
}

const isFiniteNum = (v: any) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const gbpFmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const numFmt = (maxFractionDigits = 10) => new Intl.NumberFormat('en-GB', { maximumFractionDigits: maxFractionDigits });
const money = (n: any) => gbpFmt.format(Number(n || 0));
const signedMoney = (n: any) => {
  if (!isFiniteNum(n)) return '';
  const v = Number(n);
  if (v > 0) return `+${gbpFmt.format(v)}`;
  if (v < 0) return `−${gbpFmt.format(Math.abs(v))}`;
  return gbpFmt.format(0);
};
const fmtR = (r: any) => {
  if (!isFiniteNum(r)) return '';
  const v = Number(r);
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}${Math.abs(v).toFixed(2)}R`;
};
const pnlColour = (n: any) => (!isFiniteNum(n) || Number(n) === 0 ? 'text-zinc-700 dark:text-zinc-300' : Number(n) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400');
const fmtPrice = (v: any) => (isFiniteNum(v) ? numFmt().format(Number(v)) : '');
const pounds = (v: any) => gbpFmt.format(Number(v || 0));
const perPoint = (v: any) => `${gbpFmt.format(Number(v || 0))} /pt`;
const settingMeta: Record<string, [string, (v: any) => string]> = {
  riskTarget: ['Risk per trade (target)', pounds],
  riskCap: ['Risk per trade (max)', pounds],
  dailyLossLimit: ['Daily loss limit', pounds],
  weeklyLossLimit: ['Weekly loss limit', pounds],
  stakeMin: ['Stake min', perPoint],
  stakeMax: ['Stake max', perPoint],
  balanceFloor: ['Balance floor', pounds],
  maxLosingTradesPerDay: ['Losing trades per day (max)', (v: any) => String(Number(v))],
  maxConsecutiveLosses: ['Losses in a row (max)', (v: any) => String(Number(v))],
};

// Europe/London date helpers (consistent with the calendar).
const londonKey = (iso: any) => {
  if (!iso) return '';
  const o: any = {};
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(iso)).forEach((x: any) => { o[x.type] = x.value; });
  return `${o.year}-${o.month}-${o.day}`;
};
const londonParts = (iso: any) => {
  const o: any = {};
  new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', weekday: 'long', day: 'numeric', month: 'long',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  } as any).formatToParts(new Date(iso)).forEach((x: any) => { o[x.type] = x.value; });
  return o;
};
const shortLondonDate = (iso: any) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short' }).format(new Date(iso));
const londonTime = (iso: any) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));
const dayHeading = (iso: any) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso));
const shortDayHeading = (iso: any) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(iso));
const isMidnightLondon = (iso: any) => {
  if (!iso) return false;
  const p = londonParts(iso);
  return p.hour === '00' && p.minute === '00' && p.second === '00';
};
function fmtTimeRange(openedAt: any, closedAt: any) {
  if (!openedAt) return '';
  // Imported placeholder: exactly midnight — show the date only, never 00:00.
  if (isMidnightLondon(openedAt)) return shortLondonDate(openedAt);
  const start = `${shortLondonDate(openedAt)} · ${londonTime(openedAt)}`;
  if (!closedAt || isMidnightLondon(closedAt)) return start;
  if (londonKey(openedAt) === londonKey(closedAt)) return `${start} → ${londonTime(closedAt)}`;
  return `${start} → ${shortLondonDate(closedAt)} · ${londonTime(closedAt)}`;
}
const directionWord = (d: any) => (String(d || '').toUpperCase() === 'SELL' ? 'Sell' : 'Buy');
const directionClass = (d: any) => (String(d || '').toUpperCase() === 'SELL' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400');

// Historic amendment reasons embed the real event time, e.g.
// "15m-test trail 2026-10-16 20:09 BST" — strip it for display, use it for ordering.
const EMBEDDED_DT = /(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::(\d{2}))?\s*(BST|GMT|UTC)?/;
function amendmentSortTime(a: any): number {
  const m = String(a?.reason || '').match(EMBEDDED_DT);
  if (m) {
    const offset = m[4] === 'BST' ? '+01:00' : '+00:00';
    const t = Date.parse(`${m[1]}T${m[2]}:${m[3] || '00'}${offset}`);
    if (Number.isFinite(t)) return t;
  }
  const c = Date.parse(a?.createdAt || '');
  return Number.isFinite(c) ? c : 0;
}
function amendmentLabel(a: any): string {
  const cleaned = String(a?.reason || '').replace(new RegExp(EMBEDDED_DT.source, 'g'), ' ').replace(/\s+/g, ' ').trim();
  return cleaned;
}

export default function TradingConsole() {
  const [env, setEnv] = useState('demo');
  const [theme, setTheme] = useState<Theme>(() => resolveTheme());
  const [dash, setDash] = useState<any>(null);
  const [log, setLog] = useState<any>({ trades: [], amendments: [] });
  const [settings, setSettings] = useState<any>(null);
  const [err, setErr] = useState('');
  const [igStatus, setIgStatus] = useState<any>(null);
  const [monitor, setMonitor] = useState<any>(null);
  const [monitorCfg, setMonitorCfg] = useState<any>(null);
  const [draft, setDraft] = useState<any>(null);
  const [savingMonitor, setSavingMonitor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState('');
  const [calCursor, setCalCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
  const [visibleDayCount, setVisibleDayCount] = useState(10);
  const acctId = env === 'live' ? 'KT5RA' : 'Z6997Q';
  const isLive = env === 'live';
  const qs = `accountId=${acctId}`;
  const load = useCallback(async () => {
    try { const d = await api(`/dashboard?${qs}`); setDash(d); setErr(''); } catch (e: any) { setDash(null); setErr(e.message); }
    try { setLog(await api(`/trade-log?${acctId ? `accountId=${acctId}` : ''}`)); } catch {}
    try { setSettings(await api('/settings')); } catch {}
    try { setIgStatus(await api('/ig-status')); } catch {}
    try { setMonitor(await api('/monitor/status')); } catch {}
    try { setMonitorCfg(await api('/monitor/settings')); } catch {}
  }, [qs, acctId]);
  useEffect(() => { fetchSession().then((u) => setIsAdmin(!!u?.roles.includes('admin'))).catch(() => {}); }, []);
  const igEnv = igStatus?.[env];
  const igLocked = !!igEnv?.locked;
  const clearIgLock = useCallback(async () => {
    if (!window.confirm(`Clear the IG ${env} login lock? Only do this once the IG credentials and account have been checked — the next IG request may log in once.`)) return;
    setClearing(true); setClearMsg('');
    try { await api('/ig-lockout/clear', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env }) }); setClearMsg('Lock cleared.'); await load(); }
    catch (e: any) { setClearMsg(e.message); }
    finally { setClearing(false); }
  }, [env, load]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setDraft(monitorCfg?.[env] ? { ...monitorCfg[env] } : null); }, [monitorCfg, env]);
  useEffect(() => {
    const id = setInterval(() => { api('/monitor/status').then(setMonitor).catch(() => {}); }, 15000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => { setExpandedDays({}); setVisibleDayCount(10); }, [acctId]);
  const btn = 'min-h-[48px] rounded-md px-4 font-semibold disabled:opacity-40';
  const heading = 'text-base font-semibold tracking-tight text-zinc-900 dark:text-zinc-100';
  const card = 'rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3';

  // Daily P&L calendar: sum closed trades' pnl by Europe/London day (closedAt).
  const byDay: Record<string, number> = {};
  for (const t of (log.trades || [])) {
    if (t.pnl == null || !t.closedAt) continue;
    const k = londonKey(t.closedAt);
    byDay[k] = (byDay[k] || 0) + Number(t.pnl || 0);
  }
  const calY = calCursor.getFullYear(), calM = calCursor.getMonth();
  const calPrefix = `${calY}-${String(calM + 1).padStart(2, '0')}`;
  const monthTotal = Object.entries(byDay).filter(([k]) => k.startsWith(calPrefix)).reduce((a, [, v]) => a + v, 0);
  const calOffset = (new Date(calY, calM, 1).getDay() + 6) % 7;
  const calDays = new Date(calY, calM + 1, 0).getDate();
  const calCells: any[] = [...Array(calOffset).fill(null), ...Array.from({ length: calDays }, (_, i) => i + 1)];
  const dayPnl = (d: number) => byDay[`${calPrefix}-${String(d).padStart(2, '0')}`] || 0;

  // Amendments belong to their trade (tradeId), never a global flat list.
  const amendmentsByTrade: Record<string, any[]> = {};
  (log.amendments || []).forEach((a: any, idx: number) => {
    if (a.tradeId === null || a.tradeId === undefined) return;
    const k = String(a.tradeId);
    (amendmentsByTrade[k] = amendmentsByTrade[k] || []).push({ ...a, _idx: idx, _t: amendmentSortTime(a) });
  });
  Object.values(amendmentsByTrade).forEach((list) =>
    list.sort((a, b) => (a._t - b._t) || (b._idx - a._idx)));

  // Journal: group trades by Europe/London day (closedAt, falling back to openedAt),
  // newest day first, trades within a day in the order returned (openedAt DESC).
  const dayGroups: { key: string; label: string; shortLabel: string; total: number; trades: any[] }[] = [];
  const groupByKey: Record<string, (typeof dayGroups)[number]> = {};
  for (const t of (log.trades || [])) {
    const dayIso = t.closedAt || t.openedAt;
    if (!dayIso) continue;
    const k = londonKey(dayIso);
    if (!groupByKey[k]) {
      groupByKey[k] = { key: k, label: dayHeading(dayIso), shortLabel: shortDayHeading(dayIso), total: 0, trades: [] };
      dayGroups.push(groupByKey[k]);
    }
    groupByKey[k].trades.push(t);
    if (t.closedAt && isFiniteNum(t.pnl)) groupByKey[k].total += Number(t.pnl);
  }

  // Equity curve: realised equity only, from closed trades in the log.
  // Start = current balance - all closed P&L in the log, then step at each close.
  const closedTrades = (log.trades || [])
    .filter((t: any) => t.closedAt && isFiniteNum(t.pnl))
    .slice()
    .sort((a: any, b: any) => Date.parse(a.closedAt) - Date.parse(b.closedAt));
  const balanceNow = dash?.balance?.balance;
  const equityPoints: any[] = [];
  if (isFiniteNum(balanceNow) && closedTrades.length > 0) {
    const totalClosedPnl = closedTrades.reduce((a: number, t: any) => a + Number(t.pnl), 0);
    let running = Number(balanceNow) - totalClosedPnl;
    equityPoints.push({
      idx: 0, date: shortLondonDate(closedTrades[0].closedAt),
      when: `${shortLondonDate(closedTrades[0].closedAt)} · ${londonTime(closedTrades[0].closedAt)}`,
      equity: running, market: '', pnl: null,
    });
    closedTrades.forEach((t: any, i: number) => {
      running += Number(t.pnl);
      equityPoints.push({
        idx: i + 1, date: shortLondonDate(t.closedAt),
        when: `${shortLondonDate(t.closedAt)} · ${londonTime(t.closedAt)}`,
        equity: running, market: t.market || '', pnl: Number(t.pnl),
      });
    });
  }
  const equityConfig = { equity: { label: 'Equity', color: '#34d399' } } satisfies ChartConfig;
  const equityTickIdx = equityPoints.filter((pt, i) => i === 0 || i === equityPoints.length - 1 || pt.date !== equityPoints[i - 1].date).map((pt) => pt.idx);
  const fmtEquityTick = (v: any) => {
    const n = Number(v);
    if (Math.abs(n) >= 1000) return `£${(n / 1000).toFixed(Math.abs(n) % 1000 === 0 ? 0 : 1)}k`;
    return gbpFmt.format(n).replace(/\.00$/, '');
  };

  // --- Today (Europe/London) ---
  const nowIso = new Date().toISOString();
  const todayKey = londonKey(nowIso);
  const londonWeekday = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short' }).format(new Date());
  const isWeekend = londonWeekday === 'Sat' || londonWeekday === 'Sun';
  const todayLabel = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short' }).format(new Date());
  const tradesTodayList = closedTrades.filter((t: any) => londonKey(t.closedAt) === todayKey);
  const tradesToday = tradesTodayList.length;
  const losingTodayComputed = tradesTodayList.filter((t: any) => Number(t.pnl) < 0).length;
  const losingToday = isFiniteNum(dash?.halts?.losingTradesToday) ? Number(dash.halts.losingTradesToday) : losingTodayComputed;
  const todayPnlVal = isFiniteNum(dash?.todayPnl) ? Number(dash.todayPnl) : tradesTodayList.reduce((a: number, t: any) => a + Number(t.pnl), 0);
  const dailyLimit = isFiniteNum(settings?.dailyLossLimit) ? Number(settings.dailyLossLimit) : 25;
  const lossUsed = Math.max(0, -todayPnlVal);
  const lossPct = dailyLimit > 0 ? Math.min(100, (lossUsed / dailyLimit) * 100) : 0;
  const lossBarColour = lossPct >= 100 ? 'bg-red-500' : lossPct >= 50 ? 'bg-amber-400' : 'bg-emerald-500';
  const limitLeft = Math.max(0, dailyLimit - lossUsed);
  let todayStatus = 'Active';
  let todaySub = 'Within all limits for today.';
  let todayPill = 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  let todayDot = 'bg-emerald-400';
  if (isWeekend) {
    todayStatus = 'Paused — weekend';
    todaySub = 'Markets closed · back Sunday night';
    todayPill = 'border-amber-400/25 bg-amber-400/10 text-amber-700 dark:text-amber-300';
    todayDot = 'bg-amber-400';
  } else if (dash?.halts?.halted) {
    todayStatus = 'Halted';
    const reasons: string[] = [];
    if (dash.halts.balanceHalt) reasons.push('Balance below floor');
    if (dash.halts.weeklyHalt) reasons.push('Weekly loss limit reached');
    if (dash.halts.dailyHalt) {
      if (todayPnlVal <= -dailyLimit) reasons.push('Daily loss limit reached');
      else if (isFiniteNum(settings?.maxLosingTradesPerDay) && losingToday >= Number(settings.maxLosingTradesPerDay)) reasons.push('Losing trades limit reached');
      else if (isFiniteNum(settings?.maxConsecutiveLosses) && isFiniteNum(dash.halts.consecutiveLosses) && Number(dash.halts.consecutiveLosses) >= Number(settings.maxConsecutiveLosses)) reasons.push('Losses in a row limit reached');
      else reasons.push('Daily halt');
    }
    todaySub = reasons.join(' · ') || 'Trading halted';
    todayPill = 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300';
    todayDot = 'bg-red-400';
  }

  // --- Performance / Extremes / By market (closed trades, selected account) ---
  const wins = closedTrades.filter((t: any) => Number(t.pnl) > 0);
  const losses = closedTrades.filter((t: any) => Number(t.pnl) < 0);
  const flats = closedTrades.filter((t: any) => Number(t.pnl) === 0);
  const closedCount = closedTrades.length;
  const totalPnl = closedTrades.reduce((a: number, t: any) => a + Number(t.pnl), 0);
  const winRate = closedCount ? Math.round((wins.length / closedCount) * 100) : null;
  const expectancy = closedCount ? totalPnl / closedCount : null;
  const avgWin = wins.length ? wins.reduce((a: number, t: any) => a + Number(t.pnl), 0) / wins.length : null;
  const avgLoss = losses.length ? losses.reduce((a: number, t: any) => a + Number(t.pnl), 0) / losses.length : null;
  const rTracked = closedTrades.filter((t: any) => isFiniteNum(t.rMultiple)).length;
  const bestTrade = closedCount ? closedTrades.reduce((b: any, t: any) => (Number(t.pnl) > Number(b.pnl) ? t : b), closedTrades[0]) : null;
  const worstTrade = closedCount ? closedTrades.reduce((b: any, t: any) => (Number(t.pnl) < Number(b.pnl) ? t : b), closedTrades[0]) : null;
  let maxDrawdown = 0; let drawdownPeak = 0;
  if (equityPoints.length) {
    let peak = equityPoints[0].equity;
    for (const pt of equityPoints) {
      if (pt.equity > peak) peak = pt.equity;
      const dd = peak - pt.equity;
      if (dd > maxDrawdown) { maxDrawdown = dd; drawdownPeak = peak; }
    }
  }
  const byMarketMap: Record<string, { market: string; count: number; total: number }> = {};
  for (const t of closedTrades) {
    const m = t.market || 'Unknown';
    if (!byMarketMap[m]) byMarketMap[m] = { market: m, count: 0, total: 0 };
    byMarketMap[m].count += 1; byMarketMap[m].total += Number(t.pnl);
  }
  const byMarket = Object.values(byMarketMap).sort((a, b) => b.total - a.total);
  const maxMarketAbs = byMarket.reduce((a, r) => Math.max(a, Math.abs(r.total)), 0);
  const watching: any[] = Array.isArray(dash?.watching) ? dash.watching : [];

  const stats: [string, string, string][] = [
    ['Balance', money(dash?.balance?.balance), 'text-zinc-900 dark:text-zinc-100'],
    ['Open P&L', dash ? signedMoney(dash.openPnl) : money(0), pnlColour(dash?.openPnl)],
    ["Today's P&L", dash ? signedMoney(dash.todayPnl) : money(0), pnlColour(dash?.todayPnl)],
    ['Positions', String(dash?.positions?.length || 0), 'text-zinc-900 dark:text-zinc-100'],
  ];

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <header className="sticky top-0 z-10 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/95 dark:bg-zinc-950/95 px-3 py-3">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-bold">Trading console</h1>
            <span className="flex items-center gap-2 sm:gap-3">
              <span className="hidden text-xs text-zinc-600 dark:text-zinc-400 min-[420px]:inline">{dash?.mock ? 'Mock · no IG creds' : 'IG connected'}</span>
              <a href="/backtests" className="text-xs text-zinc-700 dark:text-zinc-300 underline">Backtests</a>
              <a href="/skill" className="text-xs text-zinc-700 dark:text-zinc-300 underline">Skill</a>
              <ProfileMenu theme={theme} onThemeChange={setTheme} />
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2" role="group" aria-label="Environment">
            <button className={`${btn} ${!isLive ? 'bg-emerald-600 text-white' : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300'}`} onClick={() => setEnv('demo')}>Demo</button>
            <button className={`${btn} ${isLive ? 'bg-red-600 text-white' : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300'}`} onClick={() => setEnv('live')}>Live</button>
          </div>
          <div className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{acctId} · {env} / spread bet</div>
          {monitor?.[env] && (
            <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-400" aria-label="Position monitor">
              <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${monitor[env].alive ? 'bg-emerald-500' : monitor[env].enabled ? 'bg-amber-500' : 'bg-zinc-400'}`} />
              Monitor {monitor[env].alive ? 'live' : monitor[env].enabled ? 'not live' : 'off'}
              {' · '}stream {monitor[env].streamConnected ? 'connected' : 'down'}
              {' · '}last tick {monitor[env].lastTickAt ? new Date(monitor[env].lastTickAt).toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'none'}
              {' · '}{(monitor[env].positions || []).length} {(monitor[env].positions || []).length === 1 ? 'position' : 'positions'}
              {monitor[env].lastError ? ` · ${monitor[env].lastError}` : ''}
            </div>
          )}
        </div>
      </header>

      {isLive && <div className="border-b border-red-700 bg-red-950 px-3 py-3 text-center font-bold text-red-100">LIVE — real money</div>}

      <main className="mx-auto max-w-5xl space-y-6 px-3 py-4">
        {igLocked && (
          <div role="alert" className="rounded-md border border-amber-400 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/70 dark:text-amber-100">
            <div className="font-semibold">IG login locked after a failed login — clear in settings once credentials are verified.</div>
            <div className="mt-1 text-xs opacity-90">{igEnv.lockout?.message}</div>
            <div className="mt-1 text-xs opacity-90">The console will not try to log in to IG ({env}) until the lock is cleared{igEnv.lockout?.manual ? '' : ' or it auto-expires'}. Trades already in the journal still show.</div>
          </div>
        )}
        {err && !(igLocked && /login locked/i.test(err)) && <div className="rounded-md bg-red-100 px-3 py-2 text-sm text-red-800 dark:bg-red-900/60 dark:text-red-100">{err}</div>}

        <section className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Account summary">
          {stats.map(([l, v, colour]) => (
            <div key={l} className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3">
              <div className="text-xs text-zinc-600 dark:text-zinc-400">{l}</div>
              <div className={`mt-1 text-2xl font-bold tabular-nums sm:text-3xl ${colour}`}>{v}</div>
            </div>
          ))}
        </section>

        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className={heading}>Today</h2>
            <span className="text-xs text-zinc-500">{todayLabel}</span>
          </div>
          <div className={card}>
            <div className="flex items-center justify-between gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${todayPill}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${todayDot}`} />{todayStatus}
              </span>
              {isWeekend && <span className="text-xs text-zinc-500">Markets closed</span>}
            </div>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{todaySub}</p>
            <div className="mt-3 flex items-center justify-between text-xs text-zinc-600 dark:text-zinc-400">
              <span>Daily loss limit</span>
              <span className="font-medium tabular-nums text-zinc-900 dark:text-zinc-100">{money(lossUsed)} of {money(dailyLimit)} used</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
              <div className={`h-full rounded-full ${lossBarColour}`} style={{ width: `${lossPct}%` }} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 border-t border-zinc-200 dark:border-zinc-800 pt-3 text-center">
              <div><div className="text-base font-semibold tabular-nums">{tradesToday}</div><div className="text-[11px] text-zinc-500">trades today</div></div>
              <div><div className="text-base font-semibold tabular-nums">{losingToday}</div><div className="text-[11px] text-zinc-500">losing trades</div></div>
              <div><div className="text-base font-semibold tabular-nums">{money(limitLeft)}</div><div className="text-[11px] text-zinc-500">limit left</div></div>
            </div>
          </div>
        </section>

        <section className="space-y-2">
          <h2 className={heading}>Equity</h2>
          <div className={card}>
            {equityPoints.length < 2 ? (
              <p className="py-4 text-center text-sm text-zinc-600 dark:text-zinc-400">No closed trades yet</p>
            ) : (() => {
              const eqs = equityPoints.map((pt) => pt.equity);
              const lo = Math.min(...eqs), hi = Math.max(...eqs), range = hi - lo;
              const pad = range > 0 ? range * 0.15 : Math.max(Math.abs(lo) * 0.01, 10);
              return (
                <ChartContainer config={equityConfig} className="aspect-auto h-[170px] w-full [&_.recharts-cartesian-axis-tick_text]:fill-zinc-400">
                  <AreaChart data={equityPoints} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-equity)" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="var(--color-equity)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={theme === 'dark' ? '#27272a' : '#e4e4e7'} />
                    <XAxis
                      dataKey="idx" type="number" domain={[0, equityPoints.length - 1]}
                      ticks={equityTickIdx}
                      tickFormatter={(idx: any) => equityPoints[Number(idx)]?.date || ''}
                      tickLine={false} axisLine={false} tickMargin={8} fontSize={11}
                    />
                    <YAxis
                      domain={[lo - pad, hi + pad]}
                      tickFormatter={fmtEquityTick}
                      tickLine={false} axisLine={false} width={48} fontSize={11}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          className="border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 [&_.text-foreground]:text-zinc-900 dark:[&_.text-foreground]:text-zinc-100 [&_.text-muted-foreground]:text-zinc-600 dark:[&_.text-muted-foreground]:text-zinc-400"
                          labelFormatter={(_v: any, payload: any) => payload?.[0]?.payload?.when || ''}
                          formatter={(value: any, _name: any, item: any) => (
                            <div className="grid gap-0.5">
                              <div className="flex w-full items-center justify-between gap-4">
                                <span className="text-zinc-600 dark:text-zinc-400">Equity</span>
                                <span className="font-mono font-medium tabular-nums text-zinc-900 dark:text-zinc-100">{money(value)}</span>
                              </div>
                              {item?.payload?.market && (
                                <div className="flex w-full items-center justify-between gap-4">
                                  <span className="text-zinc-600 dark:text-zinc-400">{item.payload.market}</span>
                                  <span className={`font-mono font-medium tabular-nums ${pnlColour(item.payload.pnl)}`}>{signedMoney(item.payload.pnl)}</span>
                                </div>
                              )}
                            </div>
                          )}
                        />
                      }
                    />
                    <Area
                      dataKey="equity" type="stepAfter" stroke="var(--color-equity)" strokeWidth={2}
                      fill="url(#equityFill)" dot={false} activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ChartContainer>
              );
            })()}
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className={heading}>Performance</h2>
            <span className="text-xs text-zinc-500">{closedCount} closed {closedCount === 1 ? 'trade' : 'trades'}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3">
              <div className="text-xs text-zinc-600 dark:text-zinc-400">Win rate</div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{winRate === null ? '—' : `${winRate}%`}</div>
              <div className="mt-0.5 text-[11px] text-zinc-500">{closedCount ? `${wins.length} won · ${losses.length} lost · ${flats.length} flat` : 'No closed trades'}</div>
            </div>
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3">
              <div className="text-xs text-zinc-600 dark:text-zinc-400">Expectancy</div>
              <div className={`mt-1 text-2xl font-bold tabular-nums ${expectancy === null ? 'text-zinc-900 dark:text-zinc-100' : pnlColour(expectancy)}`}>{expectancy === null ? '—' : signedMoney(expectancy)}</div>
              <div className="mt-0.5 text-[11px] text-zinc-500">average per trade</div>
            </div>
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3">
              <div className="text-xs text-zinc-600 dark:text-zinc-400">Average win</div>
              <div className={`mt-1 text-2xl font-bold tabular-nums ${avgWin === null ? 'text-zinc-900 dark:text-zinc-100' : pnlColour(avgWin)}`}>{avgWin === null ? '—' : signedMoney(avgWin)}</div>
              <div className="mt-0.5 text-[11px] text-zinc-500">{wins.length ? `across ${wins.length} ${wins.length === 1 ? 'win' : 'wins'}` : 'No wins yet'}</div>
            </div>
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3">
              <div className="text-xs text-zinc-600 dark:text-zinc-400">Average loss</div>
              <div className={`mt-1 text-2xl font-bold tabular-nums ${avgLoss === null ? 'text-zinc-900 dark:text-zinc-100' : pnlColour(avgLoss)}`}>{avgLoss === null ? '—' : signedMoney(avgLoss)}</div>
              <div className="mt-0.5 text-[11px] text-zinc-500">{losses.length ? `across ${losses.length} ${losses.length === 1 ? 'loss' : 'losses'}` : 'No losses yet'}</div>
            </div>
          </div>
          {closedCount > 0 && rTracked < closedCount && (
            <p className="text-[11px] leading-relaxed text-zinc-500">ⓘ R tracked on {rTracked} of {closedCount} trades — averages in £</p>
          )}
        </section>

        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className={heading}>Extremes</h2>
            <span className="text-xs text-zinc-500">all time · {env}</span>
          </div>
          <div className={`${card} divide-y divide-zinc-200 dark:divide-zinc-800 !p-0`}>
            {[
              { label: 'Best trade', trade: bestTrade, bar: 'bg-emerald-500' },
              { label: 'Worst trade', trade: worstTrade, bar: 'bg-red-500' },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-2 px-3 py-2.5">
                <div className="flex items-center gap-2.5">
                  <span className={`h-8 w-[3px] flex-none rounded ${row.bar}`} />
                  <div><div className="text-xs text-zinc-600 dark:text-zinc-400">{row.label}</div><div className="text-sm font-medium">{row.trade ? row.trade.market : '—'}</div></div>
                </div>
                <div className={`text-right text-base font-semibold tabular-nums ${row.trade ? pnlColour(row.trade.pnl) : 'text-zinc-600 dark:text-zinc-400'}`}>
                  {row.trade ? signedMoney(row.trade.pnl) : '—'}
                  {row.trade && <small className="block text-[11px] font-normal text-zinc-500">{shortLondonDate(row.trade.closedAt)}</small>}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between gap-2 px-3 py-2.5">
              <div className="flex items-center gap-2.5">
                <span className="h-8 w-[3px] flex-none rounded bg-red-500/60" />
                <div><div className="text-xs text-zinc-600 dark:text-zinc-400">Max drawdown</div><div className="text-sm font-medium">{drawdownPeak ? `from ${money(drawdownPeak)} peak` : '—'}</div></div>
              </div>
              <div className={`text-right text-base font-semibold tabular-nums ${maxDrawdown ? 'text-red-600 dark:text-red-400' : 'text-zinc-600 dark:text-zinc-400'}`}>
                {closedCount ? signedMoney(-maxDrawdown) : '—'}
                {closedCount > 0 && drawdownPeak > 0 && <small className="block text-[11px] font-normal text-zinc-500">{(maxDrawdown / drawdownPeak * 100).toFixed(1)}% of peak</small>}
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-2">
          <h2 className={heading}>Calendar</h2>
          <div className={card}>
            <div className="flex items-center justify-between gap-2">
              <button className="min-h-[40px] rounded-md bg-zinc-200 dark:bg-zinc-800 px-3 text-sm font-semibold" onClick={() => setCalCursor(new Date(calY, calM - 1, 1))}>‹ Prev</button>
              <h3 className="font-semibold">{calCursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</h3>
              <button className="min-h-[40px] rounded-md bg-zinc-200 dark:bg-zinc-800 px-3 text-sm font-semibold" onClick={() => setCalCursor(new Date(calY, calM + 1, 1))}>Next ›</button>
            </div>
            <div className="mt-2 text-center text-sm">
              Month total:{' '}
              <span className={`font-bold tabular-nums ${pnlColour(monthTotal)}`}>{signedMoney(monthTotal)}</span>
            </div>
            <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs text-zinc-600 dark:text-zinc-400">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} className="py-1 font-semibold">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calCells.map((d, i) => {
                if (d == null) return <div key={`e-${i}`} />;
                const v = dayPnl(d);
                return (
                  <div key={d} className={`min-h-[52px] rounded-md p-1 text-center ${v > 0 ? 'bg-emerald-100 dark:bg-emerald-900/60' : v < 0 ? 'bg-red-100 dark:bg-red-900/60' : 'bg-zinc-50 dark:bg-zinc-950'}`}>
                    <div className="text-xs text-zinc-700 dark:text-zinc-300">{d}</div>
                    {v !== 0 && <div className={`mt-1 text-[11px] font-bold leading-tight tabular-nums ${v > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>{signedMoney(v)}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className={heading}>Results by market</h2>
            <span className="text-xs text-zinc-500">{byMarket.length} {byMarket.length === 1 ? 'market' : 'markets'}</span>
          </div>
          <div className={card}>
            {byMarket.length === 0 ? (
              <p className="py-2 text-center text-sm text-zinc-600 dark:text-zinc-400">No closed trades yet</p>
            ) : (
              <>
                <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
                  {byMarket.map((r) => (
                    <div key={r.market} className="grid grid-cols-[118px_1fr_82px] items-center gap-2.5 py-2">
                      <div><div className="text-[13px] font-medium leading-tight">{r.market}</div><div className="text-[11px] text-zinc-500">{r.count} {r.count === 1 ? 'trade' : 'trades'}</div></div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                        <div className={`h-full rounded-full ${r.total > 0 ? 'bg-emerald-500' : r.total < 0 ? 'bg-red-500' : 'bg-zinc-500'}`} style={{ width: `${maxMarketAbs ? Math.max(2, Math.abs(r.total) / maxMarketAbs * 100) : 0}%` }} />
                      </div>
                      <div className={`text-right text-[13px] font-semibold tabular-nums ${pnlColour(r.total)}`}>{signedMoney(r.total)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-1 flex items-center justify-between border-t border-zinc-300 dark:border-zinc-700 pt-2.5 text-xs text-zinc-600 dark:text-zinc-400">
                  <span>Total · {closedCount} {closedCount === 1 ? 'trade' : 'trades'}</span>
                  <span className={`font-semibold tabular-nums ${pnlColour(totalPnl)}`}>{signedMoney(totalPnl)}</span>
                </div>
              </>
            )}
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className={heading}>Watching next</h2>
            <span className="text-xs text-zinc-500">read-only</span>
          </div>
          <div className={card}>
            {watching.length === 0 ? (
              <p className="py-2 text-center text-sm text-zinc-600 dark:text-zinc-400">Nothing being watched</p>
            ) : (
              <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {watching.map((w: any) => (
                  <div key={w.market} className="flex items-center justify-between gap-2 py-2.5 first:pt-0 last:pb-0">
                    <div><div className="text-sm font-medium">{w.market}</div><div className="text-xs text-zinc-500">{w.note}</div></div>
                    <span className={`flex-none rounded-md border px-2 py-1 text-[11px] ${w.status === 'Watching' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-zinc-300 dark:border-zinc-700 bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'}`}>{w.status}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-[11px] text-zinc-500">Set by Harry at market open</p>
          </div>
        </section>

        <section className="space-y-2">
          <h2 className={heading}>Positions</h2>
          <div className="space-y-2">
            {(dash?.positions || []).length === 0
              ? <p className="text-sm text-zinc-600 dark:text-zinc-400">No open positions</p>
              : (dash.positions).map((p: any) => (
                <div key={p.dealId} className={card}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-base font-semibold">{p.market || p.epic}</span>
                    <span className={`text-xl font-bold tabular-nums ${pnlColour(p.pnl)}`}>{signedMoney(p.pnl)}</span>
                  </div>
                  <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                    <span className={`font-medium ${directionClass(p.direction)}`}>{directionWord(p.direction)}</span>
                    {isFiniteNum(p.size) && <> · {p.size}/pt</>}
                    {isFiniteNum(p.entryPrice) && <> · entry {fmtPrice(p.entryPrice)}</>}
                    {isFiniteNum(p.stopLevel) && <> · stop {fmtPrice(p.stopLevel)}</>}
                    {isFiniteNum(p.currentPrice) && <> · now {fmtPrice(p.currentPrice)}</>}
                    {isFiniteNum(p.rMultiple) && <> · {fmtR(p.rMultiple)}</>}
                  </div>
                  {(() => {
                    const watched = (monitor?.[env]?.positions || []).find((row: any) => row.dealId === p.dealId);
                    if (!watched) return null;
                    return (
                      <div className="mt-1 text-xs text-zinc-500">
                        {watched.managedByMonitor ? 'Managed by monitor' : 'Monitor not managing this position'}
                        {isFiniteNum(watched.mfeR) && <> · MFE {fmtR(watched.mfeR)}</>}
                      </div>
                    );
                  })()}
                </div>
              ))}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className={heading}>Journal</h2>
          {dayGroups.length === 0 && <p className="text-sm text-zinc-600 dark:text-zinc-400">No trades yet</p>}
          {dayGroups.slice(0, visibleDayCount).map((g, dayIdx) => {
            const expanded = expandedDays[g.key] ?? dayIdx < 3;
            return (
            <div key={g.key} className="space-y-2">
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpandedDays((prev) => ({ ...prev, [g.key]: !(prev[g.key] ?? dayIdx < 3) }))}
                className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md px-0.5 py-1 text-left"
              >
                <span className="flex items-center gap-1.5">
                  <span aria-hidden="true" className="text-xs text-zinc-500">{expanded ? '▾' : '▸'}</span>
                  <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{g.shortLabel}</span>
                  <span className="text-xs text-zinc-500">· {g.trades.length} {g.trades.length === 1 ? 'trade' : 'trades'}</span>
                </span>
                <span className={`text-sm font-bold tabular-nums ${pnlColour(g.total)}`}>{signedMoney(g.total)}</span>
              </button>
              {expanded && g.trades.map((t: any) => {
                const moves = amendmentsByTrade[String(t.id)] || [];
                const isOpen = t.status === 'open';
                const priceLine = isFiniteNum(t.entryPrice)
                  ? (isFiniteNum(t.exitPrice) && !isOpen ? `${fmtPrice(t.entryPrice)} → ${fmtPrice(t.exitPrice)}` : fmtPrice(t.entryPrice))
                  : '';
                const timeLine = fmtTimeRange(t.openedAt, isOpen ? null : t.closedAt);
                return (
                  <article key={t.id} className={card}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-base font-semibold">{t.market}</span>
                      {isOpen ? (
                        <span className="text-base font-bold tabular-nums text-zinc-800 dark:text-zinc-200">
                          Open{isFiniteNum(t.pnl) && <span className={pnlColour(t.pnl)}> · {signedMoney(t.pnl)}</span>}
                        </span>
                      ) : isFiniteNum(t.pnl) ? (
                        <span className={`text-base font-bold tabular-nums ${pnlColour(t.pnl)}`}>
                          {signedMoney(t.pnl)}{isFiniteNum(t.rMultiple) && <span> · {fmtR(t.rMultiple)}</span>}
                        </span>
                      ) : (
                        <span className="text-base font-bold text-zinc-700 dark:text-zinc-300">Closed</span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                      <span className={`font-medium ${directionClass(t.direction)}`}>{directionWord(t.direction)}</span>
                      {isFiniteNum(t.size) && <> · {t.size}/pt</>}
                      {priceLine && <> · {priceLine}</>}
                      {isFiniteNum(t.stopLevel) && <> · stop {fmtPrice(t.stopLevel)}</>}
                      {timeLine && <><br />{timeLine}</>}
                    </div>
                    {t.reason && <p className="mt-2 text-sm leading-relaxed text-zinc-800 dark:text-zinc-200">{t.reason}</p>}
                    <TradeChart tradeId={t.id} theme={theme} direction={t.direction} />
                    {moves.length > 0 && (() => {
                      const isSell = String(t.direction || '').toUpperCase() === 'SELL';
                      const accent = isSell ? '#38bdf8' : '#34d399';
                      const chartConfig = { stop: { label: 'Stop level', color: accent } } satisfies ChartConfig;
                      const startMs = (!t.openedAt || isMidnightLondon(t.openedAt)) ? moves[0]._t : (Date.parse(t.openedAt) || moves[0]._t);
                      const points: any[] = [
                        { idx: 0, time: londonTime(new Date(startMs).toISOString()), stop: Number(moves[0].oldStop), label: 'Start' },
                        ...moves.map((a: any, i: number) => ({
                          idx: i + 1,
                          time: londonTime(new Date(a._t).toISOString()),
                          stop: Number(a.newStop),
                          label: amendmentLabel(a) || 'Stop move',
                        })),
                      ].filter((pt) => Number.isFinite(pt.stop));
                      if (points.length < 2) return null;
                      const stops = points.map((pt) => pt.stop);
                      const lo = Math.min(...stops), hi = Math.max(...stops), range = hi - lo;
                      const pad = range > 0 ? range * 0.15 : Math.max(Math.abs(lo) * 0.001, 0.0001);
                      const dec = Math.min(5, Math.max(0, ...stops.map((v) => (String(v).split('.')[1] || '').length)));
                      const fmtStop = (v: any) => new Intl.NumberFormat('en-GB', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(Number(v));
                      return (
                        <details className="mt-3 border-t border-zinc-200 dark:border-zinc-800 pt-2">
                          <summary className="cursor-pointer py-1 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                            Stop moves ({moves.length})
                          </summary>
                          <div className="pb-1 pt-1">
                            <ChartContainer config={chartConfig} className="aspect-auto h-[150px] w-full [&_.recharts-cartesian-axis-tick_text]:fill-zinc-400">
                              <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                                <CartesianGrid vertical={false} stroke={theme === 'dark' ? '#27272a' : '#e4e4e7'} />
                                <XAxis
                                  dataKey="idx" type="number" domain={[0, points.length - 1]}
                                  ticks={points.map((pt) => pt.idx)}
                                  tickFormatter={(idx: any) => points[Number(idx)]?.time || ''}
                                  tickLine={false} axisLine={false} tickMargin={8} fontSize={11}
                                />
                                <YAxis
                                  domain={[lo - pad, hi + pad]}
                                  tickFormatter={fmtStop}
                                  tickLine={false} axisLine={false} width={56} fontSize={11}
                                />
                                <ChartTooltip
                                  content={
                                    <ChartTooltipContent
                                      className="border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 [&_.text-foreground]:text-zinc-900 dark:[&_.text-foreground]:text-zinc-100 [&_.text-muted-foreground]:text-zinc-600 dark:[&_.text-muted-foreground]:text-zinc-400"
                                      labelFormatter={(_v: any, payload: any) => payload?.[0]?.payload?.time || ''}
                                      formatter={(value: any, _name: any, item: any) => (
                                        <div className="flex w-full items-center justify-between gap-4">
                                          <span className="text-zinc-600 dark:text-zinc-400">{item?.payload?.label || 'Stop level'}</span>
                                          <span className="font-mono font-medium tabular-nums text-zinc-900 dark:text-zinc-100">{fmtStop(value)}</span>
                                        </div>
                                      )}
                                    />
                                  }
                                />
                                <Line
                                  dataKey="stop" type="stepAfter" stroke="var(--color-stop)" strokeWidth={2}
                                  dot={{ r: 3, fill: accent, strokeWidth: 0 }} activeDot={{ r: 4 }}
                                  isAnimationActive={false}
                                />
                              </LineChart>
                            </ChartContainer>
                            <p className="mt-1 text-sm tabular-nums text-zinc-600 dark:text-zinc-400">
                              Stop {fmtPrice(moves[0].oldStop)} → {fmtPrice(moves[moves.length - 1].newStop)} · {moves.length} {moves.length === 1 ? 'move' : 'moves'}
                            </p>
                            <ul className="mt-1 space-y-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                              {moves.map((a: any) => (
                                <li key={a.id || a._idx}>
                                  {a._t ? londonTime(new Date(a._t).toISOString()) : ''} {amendmentLabel(a) || 'Stop move'}
                                  {isFiniteNum(a.mfeR) && <> · MFE {fmtR(a.mfeR)}{isFiniteNum(a.mfePrice) ? ` at ${fmtPrice(a.mfePrice)}` : ''}</>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </details>
                      );
                    })()}
                  </article>
                );
              })}
            </div>
            );
          })}
          {dayGroups.length > 0 && visibleDayCount < dayGroups.length && (
            <button
              type="button"
              onClick={() => setVisibleDayCount((c) => c + 10)}
              className="min-h-[48px] w-full rounded-md bg-zinc-200 dark:bg-zinc-800 px-4 text-sm font-semibold text-zinc-700 dark:text-zinc-300"
            >
              Show older days
            </button>
          )}
          {dayGroups.length > 0 && (
            <p className="text-center text-xs text-zinc-500">{Math.min(visibleDayCount, dayGroups.length)} of {dayGroups.length} {dayGroups.length === 1 ? 'day' : 'days'} shown</p>
          )}
        </section>

        {settings && (
          <section className={card}>
            <h2 className={heading}>Settings</h2>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {Object.keys(settings).map((k) => {
                const meta = settingMeta[k];
                return (
                  <div key={k} className="rounded-md border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-3 py-2">
                    <div className="text-xs text-zinc-600 dark:text-zinc-400">{meta ? meta[0] : k}</div>
                    <div className="mt-1 text-base font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{meta ? meta[1](settings[k]) : String(settings[k])}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 rounded-md border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-3 py-3" aria-label="IG connection">
              <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">IG connection ({env})</div>
              {igEnv ? (
                <div className="mt-1 space-y-1 text-xs text-zinc-600 dark:text-zinc-400">
                  <div>{igEnv.mock ? 'Mock mode — no IG calls.' : igLocked ? 'Login locked.' : igEnv.cached ? `Session cached (${igEnv.ageH}h old, idle ${igEnv.idleMin} min) — reused across restarts.` : 'No cached session — next IG request logs in once.'}</div>
                  <div>Last login: {igEnv.lastLoginAt ? new Date(igEnv.lastLoginAt).toLocaleString('en-GB', { timeZone: 'Europe/London' }) + ' London' : 'none recorded'} · logins {igEnv.loginsOk} ok / {igEnv.loginsFailed} failed</div>
                  {igLocked && <div className="text-amber-800 dark:text-amber-200">{igEnv.lockout?.message}</div>}
                  {igLocked && (isAdmin ? (
                    <button className="mt-2 min-h-[44px] rounded-md bg-amber-600 px-4 text-sm font-semibold text-white disabled:opacity-40" disabled={clearing} onClick={clearIgLock}>{clearing ? 'Clearing…' : `Clear IG ${env} login lock`}</button>
                  ) : <div>Only an admin can clear the lock.</div>)}
                  {clearMsg && <div>{clearMsg}</div>}
                </div>
              ) : <div className="mt-1 text-xs text-zinc-500">Status unavailable.</div>}
            </div>
            <div className="mt-4 rounded-md border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-3 py-3" aria-label="Position monitor settings">
              <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Position monitor ({env})</div>
              <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">Streams open spread-bet prices and tightens the broker stop. Demo can be switched on here. Live stays off until an admin confirms it. The monitor reuses the cached IG session and does not log in again to reconnect.</p>
              {draft && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {([
                    ['lockTriggerR', 'Lock trigger (R)'],
                    ['lockLevelR', 'Lock level (R)'],
                    ['trailAtrMultiple', 'Trail (×ATR)'],
                    ['minStopStepR', 'Min step (R)'],
                  ] as [string, string][]).map(([key, label]) => (
                    <label key={key} className="text-xs text-zinc-600 dark:text-zinc-400">
                      {label}
                      <input
                        className="mt-1 min-h-[44px] w-full rounded-md border border-zinc-300 bg-white px-2 text-base text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        inputMode="decimal"
                        disabled={!isAdmin}
                        value={draft[key] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [key]: e.target.value === '' ? '' : Number(e.target.value) })}
                      />
                    </label>
                  ))}
                  <label className="text-xs text-zinc-600 dark:text-zinc-400">
                    Take profit (R, blank = off)
                    <input className="mt-1 min-h-[44px] w-full rounded-md border border-zinc-300 bg-white px-2 text-base text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100" inputMode="decimal" disabled={!isAdmin} value={draft.takeProfitR ?? ''} onChange={(e) => setDraft({ ...draft, takeProfitR: e.target.value === '' ? null : Number(e.target.value) })} />
                  </label>
                  <label className="text-xs text-zinc-600 dark:text-zinc-400">
                    No follow-through (minutes, blank = off)
                    <input className="mt-1 min-h-[44px] w-full rounded-md border border-zinc-300 bg-white px-2 text-base text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100" inputMode="numeric" disabled={!isAdmin} value={draft.noFollowThroughMinutes ?? ''} onChange={(e) => setDraft({ ...draft, noFollowThroughMinutes: e.target.value === '' ? null : Number(e.target.value) })} />
                  </label>
                </div>
              )}
              {isAdmin ? (
                <div className="mt-3 flex flex-col gap-2">
                  <button
                    type="button"
                    className="min-h-[44px] rounded-md bg-zinc-900 px-4 text-sm font-semibold text-white disabled:opacity-40 dark:bg-zinc-100 dark:text-zinc-900"
                    disabled={savingMonitor || !draft}
                    onClick={async () => {
                      setSavingMonitor(true); setClearMsg('');
                      try {
                        const saved = await api('/monitor/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env, ...draft, enabled: !!draft.enabled, confirmLive: false }) });
                        setMonitorCfg(saved); setClearMsg('Monitor settings saved.');
                      } catch (e: any) { setClearMsg(e.message); }
                      finally { setSavingMonitor(false); }
                    }}
                  >{savingMonitor ? 'Saving…' : 'Save monitor settings'}</button>
                  <button
                    type="button"
                    className={`min-h-[44px] rounded-md px-4 text-sm font-semibold text-white disabled:opacity-40 ${draft?.enabled ? 'bg-zinc-500' : env === 'live' ? 'bg-red-700' : 'bg-emerald-700'}`}
                    disabled={savingMonitor || !draft}
                    onClick={async () => {
                      const enabled = !draft?.enabled;
                      if (env === 'live' && enabled && !window.confirm('Enable the LIVE position monitor on KT5RA? It will stream prices and can amend stops on the live spread-bet account. It will not open positions.')) return;
                      setSavingMonitor(true); setClearMsg('');
                      try {
                        const saved = await api('/monitor/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env, ...draft, enabled, confirmLive: env === 'live' && enabled }) });
                        setMonitorCfg(saved);
                        setMonitor(await api('/monitor/status'));
                        setClearMsg(enabled ? `${env} monitor enabled.` : `${env} monitor disabled.`);
                      } catch (e: any) { setClearMsg(e.message); }
                      finally { setSavingMonitor(false); }
                    }}
                  >{draft?.enabled ? `Disable ${env} monitor` : `Enable ${env} monitor`}</button>
                </div>
              ) : <p className="mt-2 text-xs text-zinc-500">Only an admin can change the monitor. It is {draft?.enabled ? 'enabled' : 'off'} for {env}.</p>}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
